import { Link } from "react-router-dom";
import { ArrowUpRight, Star } from "lucide-react";
import { servicesContent, servicesCategories } from "@/data/servicesContent";

const categoryEntries = Object.entries(servicesCategories);

export default function ServicesOverview() {
  return (
    <div className="bg-[#0B0C10]" data-testid="services-overview">
      {/* Hero */}
      <section className="relative border-b border-[#1F2833] py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 te-grid-bg-dense opacity-30" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C] mb-6">// SERVICES</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tighter leading-[0.95] max-w-5xl">
            Growth. Private AI. <br />
            <span className="text-[#FF3355]">Governance.</span>
          </h1>
          <p className="mt-8 text-[#C5C6C7] max-w-3xl leading-relaxed text-lg">
            Three engines, one accountable firm. Marketing retainers, on-prem LLM deployment for regulated enterprises,
            and fixed-fee AI risk audits that convert into deployment engagements.
          </p>
          <div className="mt-10 flex flex-wrap gap-3 font-mono text-[11px] uppercase tracking-[0.22em]">
            {categoryEntries.map(([key, cat]) => (
              <a
                key={key}
                href={`#${key}`}
                data-testid={`services-jump-${key}`}
                className="border border-[#1F2833] px-4 py-2 hover:border-[#FF3355] text-[#C5C6C7] hover:text-[#F5F5F7]"
              >
                // {cat.label} ↓
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Category sections */}
      {Object.entries(servicesCategories).map(([key, cat]) => (
        <section key={key} id={key} className="py-16 lg:py-20 border-b border-[#1F2833] scroll-mt-24">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="mb-10">
              <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C]">{cat.kicker}</div>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter">{cat.label}</h2>
              <p className="mt-3 text-[#C5C6C7] max-w-3xl">{cat.tagline}</p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-[#1F2833] border border-[#1F2833]">
              {cat.slugs.map((slug) => {
                const svc = servicesContent[slug];
                if (!svc) return null;
                return (
                  <Link
                    key={slug}
                    to={`/services/${slug}`}
                    data-testid={`services-card-${slug}`}
                    className={`relative bg-[#0B0C10] p-8 lg:p-10 flex flex-col te-glow-hover border border-transparent hover:border-[#B91C2C] group ${
                      svc.popular ? "lg:bg-[#0d1117]" : ""
                    }`}
                  >
                    {svc.popular && (
                      <div className="absolute top-4 right-4 inline-flex items-center gap-1.5 bg-[#FF3355] text-[#0B0C10] px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.22em] font-bold">
                        <Star size={10} fill="currentColor" /> Popular
                      </div>
                    )}
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#B91C2C]">{svc.kicker}</div>
                    <h3 className="mt-4 font-display text-2xl font-bold tracking-tighter leading-tight">{svc.name}</h3>
                    <p className="mt-2 font-mono text-xs text-[#C5C6C7]">{svc.subtitle}</p>

                    <div className="mt-6 border-l-2 border-[#FF3355] pl-4">
                      <div className="font-display text-2xl font-bold text-[#FF3355] leading-tight">{svc.priceLabel}</div>
                      <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[#C5C6C7]/70">{svc.priceNote}</div>
                    </div>

                    <p className="mt-6 text-sm text-[#F5F5F7] leading-relaxed flex-1">{svc.tagline}</p>

                    <div className="mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.22em] text-[#FF3355] group-hover:text-[#F5F5F7]">
                      View details <ArrowUpRight size={14} />
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      ))}

      {/* Bottom CTA */}
      <section className="py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8 border border-[#1F2833] bg-[#0d1117] p-8 lg:p-12">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C] mb-3">// NOT SURE WHICH SERVICE?</div>
            <h3 className="font-display text-2xl lg:text-4xl font-bold tracking-tight max-w-2xl">
              Talk to a founder. 30 minutes. No pitch deck.
            </h3>
          </div>
          <Link
            to="/terminal"
            data-testid="services-bottom-cta"
            className="inline-flex items-center gap-2 bg-[#B91C2C] text-[#F5F5F7] px-7 py-4 font-mono text-xs uppercase tracking-[0.22em] font-bold hover:bg-[#FF3355] transition-colors w-fit"
          >
            Book Strategy Call <ArrowUpRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
}
